import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { Component as BrComponent, Page } from '@bloomreach/spa-sdk';
import { AnalyticsService, MarketoFormConfig } from '@frk/eds-components';
import { MarketoPreferencesResult } from '@frk/eds-components/lib/models/marketo-form.model';
import { LOCATION } from '@ng-web-apis/common';
import { OneTrustGroups, OneTrustService } from '@services/one-trust.service';
import { IUserProfile, IUserProfileInfo } from '@services/profile.interface';
import { ProfileService } from '@services/profile.service';
import { Logger } from '@utils/logger';
import { noop, Subject } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { UserPreferencesService } from './user-preferences.service';
import { CookieService } from 'ngx-cookie-service';

const logger = Logger.getLogger('MarketoFormComponent');

@Component({
  selector: 'ft-marketo-form',
  templateUrl: './marketo-form.component.html',
  styleUrls: ['./marketo-form.component.scss'],
})
export class MarketoFormComponent
  implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  /**
   * The Bloomreach component
   */
  @Input() component!: BrComponent;

  /**
   * The Bloomreach page
   */
  @Input() page!: Page;

  /**
   * Marketo Form Id from parent component
   */
  @Input() externalId: string;

  /**
   * Success title from parent component
   */
  @Input() externalSuccessTitle: string;

  /**
   * Success message from parent component
   */
  @Input() externalSuccessMessage: string;

  /**
   * Margin bottom from parent component
   */
  @Input() externalMarginBottom: string;

  /**
   * Marketo configuration
   */
  formConfig: MarketoFormConfig = {
    marketoJSSrc: '/assets/js/marketo/forms2.min.js',
    marketoUrl: '//pages.to.franklintempleton.com',
    munchkinId: '848-IAP-939',
  };

  public containerClass: string;
  public disclaimer: string;
  public showButtonUnderneath = false;
  public titleColor: string;
  public componentId: string;
  /**
   * User subscription data for preferences
   */
  userSubscriptionData?: MarketoPreferencesResult;

  /**
   * used to remove subscriptions when component is destroyed
   */
  private unsubscribe$: Subject<void> = new Subject<void>();

  preferencesLoaded: boolean;
  /**
   * save profile pre fill data
   */
  prefillData: IUserProfileInfo;

  /**
   * do user privacy preferences allow us to show form
   */
  canShowForm = true;
  marketoFormPrivacy = '';
  firstClick = true;
  public backgroundTheme: string;

  /**
   * Constructor
   */
  constructor(
    private analytics: AnalyticsService,
    private router: Router,
    private profileService: ProfileService,
    private userPreferencesService: UserPreferencesService,
    private oneTrustService: OneTrustService,
    private changeDetectorRef: ChangeDetectorRef,
    private cookieService: CookieService,
    @Inject(LOCATION) readonly locationRef: Location,
    @Inject(DOCUMENT) private documentRef: Document
  ) {}

  /**
   * Ng On Init
   */
  ngOnInit(): void {
    logger.debug('Marketo Form ngOnInit');
    if (this.component) {
      this.loadValues();
    }

    this.oneTrustService
      .getOneTrustGroups$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((groups) => {
        logger.debug(groups);
        this.setMarketoCookie(groups);
      });

    this.profileService
      .getUserProfile$()
      ?.pipe(
        takeUntil(this.unsubscribe$),
        filter((profile: IUserProfile): boolean => {
          if (profile.isLoggedIn) {
            this.prefillData = profile?.profileInfo;
            return !!profile?.profileInfo?.email;
          } else {
            return true;
          }
        })
      )
      .subscribe((profileData: IUserProfile): void => {
        this.userPreferencesService
          .getEmailPreferences$(profileData?.profileInfo?.email)
          .pipe(
            takeUntil(this.unsubscribe$),
            catchError((err) => {
              this.preferencesLoaded = true;
              this.changeDetectorRef.detectChanges();
              return [];
            })
          )
          .subscribe(
            (data: MarketoPreferencesResult) => {
              if (data?.result?.length > 0) {
                // Agreed approach: Use the record with not empty or the highest number ID.
                // Service should return results always as an array & front-end component can sort/filter results on "id".
                const sortedResults = data.result.sort((a, b) => b.id - a.id);
                this.userSubscriptionData = data;
                this.userSubscriptionData.result[0] = sortedResults[0];
              }
            },
            noop,
            () => {
              this.preferencesLoaded = true;
              this.changeDetectorRef.detectChanges();
            }
          );
      });
  }

  setMarketoCookie(groups: OneTrustGroups[]): void {
    if (!groups.includes(OneTrustGroups.Functional)) {
      this.documentRef.cookie = 'mkto_opt_out=id:true; path=/';
    }

    if (groups.includes(OneTrustGroups.Functional)) {
      let cookieDomain = this.documentRef.domain;

      // delete cookie for current domain - in case it is 1 segment domain like localhost
      this.documentRef.cookie =
        'mkto_opt_out=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';

      while (
        this.documentRef.cookie.includes('mkto_opt_out') &&
        cookieDomain.replace(/^\./, '').split('.').length >= 2
      ) {
        this.documentRef.cookie =
          'mkto_opt_out=; expires=Thu, 01 Jan 1970 00:00:00 UTC; domain = ' +
          cookieDomain +
          '; path=/;';
        // we remove subdomains until all cookies are deleted
        cookieDomain = this.shiftDomainSegment(cookieDomain);
      }
    }
  }

  shiftDomainSegment(cookieDomain: string): string {
    // we slice 1 segment, ignoring leading . to remove empty dot as well
    return '.' + cookieDomain.replace(/^\./, '').split('.').slice(1).join('.');
  }

  ngAfterViewInit() {
    this.trackFormViewAnalytics();
  }

  ngOnChanges(): void {}
  /**
   * Ng On Destroy
   */
  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  public setBackgroundThemeOfForm() {
    if (this.background === 'gradient') {
      this.backgroundTheme = 'gradient';
    } else if (this.background && this.background !== 'primary') {
      this.backgroundTheme = 'primary';
    } else {
      this.backgroundTheme = 'secondary-a';
    }
  }

  loadValues(): void {
    this.containerClass = this.component?.getParameters()?.containerClass;
    this.disclaimer = this.component?.getParameters()?.disclaimer;
    // get component id from BR
    this.componentId = this.component?.getParameters()?.componentId;
    this.showButtonUnderneath = this.component?.getParameters()?.unsubscribeAllAfterDisclaimer;
    if (this.background === 'default') {
      this.titleColor = this.component?.getParameters()?.titleColor;
    }
    this.setBackgroundThemeOfForm();
  }

  trackFormViewAnalytics(): void {
    this.analytics.trackEvent({
      event: 'form_view',
      detailed_event: 'Form Viewed',
      event_data: {
        form_id: this.id,
        form_name: `mktoForm_${this.id}`,
        form_type: 'Lead',
      },
    });
  }

  //////////////////
  // TODO: don't use `get` props in templates, as they're a performance issue
  // They get recalculated every time change detection runs:
  // https://medium.com/showpad-engineering/why-you-should-never-use-function-calls-in-angular-template-expressions-e1a50f9c0496
  //////////////////

  /**
   * Get Marketo Form Id
   */
  get id(): string {
    if (this.externalId) {
      return this.externalId;
    }
    return this.component?.getParameters()?.id;
    // return '865'; //email subscription MK2 form ID
  }

  /**
   * Get Marketo Success Title
   */
  get successTitle(): string {
    if (this.externalSuccessTitle) {
      return;
    }
    return this.component?.getParameters()?.successTitle;
  }
  /**
   * Get Marketo Success Msg
   */
  get successMsg(): string {
    if (this.externalSuccessMessage) {
      return this.externalSuccessMessage;
    }
    return this.component?.getParameters()?.successMsg;
  }

  /**
   * Get Margin Bottom parameter
   */
  get marginBottom(): string {
    if (this.externalMarginBottom) {
      return this.externalMarginBottom;
    }
    const marginBottom: string = this.component?.getParameters()?.marginBottom;
    return marginBottom;
  }

  /**
   * Get Marketo unsubscribe button flag
   */
  get hasUnsubscribeButton(): boolean {
    return this.component?.getParameters()?.hasUnsubscribeButton;
  }
  /**
   * Get Marketo unsubscribe button text
   */
  get unsubscribeButtonText(): string {
    return this.component?.getParameters()?.unsubscribeButtonText;
  }

  /**
   * Get Marketo Success Url
   */
  get successUrl(): string {
    return this.component?.getParameters()?.successUrl;
  }

  /**
   * Get Marketo Title
   */
  get title(): string {
    return this.component?.getParameters()?.title;
  }

  /**
   * Get Marketo Summary
   */
  get summary(): string {
    return this.component?.getParameters()?.summary;
  }

  /**
   * Get background  Class
   */
  get background(): string {
    return this.component?.getParameters()?.background;
  }

  /**
   * Get Retain Form After Submission value
   */
  get retainFormAfterSubmission(): boolean {
    const isFormRetained: boolean = this.component?.getParameters()
      ?.retainFormAfterSubmission
      ? this.component?.getParameters()?.retainFormAfterSubmission
      : false;
    return isFormRetained;
  }

  /**
   * Analytics tracker for form
   */
  formAnalytics(): void {
    this.analytics.trackEvent({
      event: 'leadformSubmit',
      LeadFormAction: `mktoForm_${this.id}`,
      LeadFormCategory: 'Lead',
      LeadFormCheckbox: '',
      LeadFormLabel: 'Submit',
      LeadFormURL: this.router.url,
      LeadFormID: this.id,
    });

    this.analytics.trackEvent({
      event: 'form_complete',
      detailed_event: 'Form Complete',
      event_data: {
        form_id: this.id,
        form_name: `mktoForm_${this.id}`,
        form_type: 'Lead',
      },
    });
  }

  formClickAnalytics() {
    if (this.firstClick) {
      this.firstClick = false;
      this.analytics.trackEvent({
        event: 'form_start',
        detailed_event: 'Form Started',
        event_data: {
          form_id: this.id,
          form_name: `mktoForm_${this.id}`,
          form_type: 'Lead',
        },
      });
    }
  }
}
